"use client";

import { API_WS_URL } from "@/consts/sys_config";
import BigMath from "@yn/money/math";
import { ConnectManager, CryptoPriceSubscriptionManager, SOCKET_KEY } from "@yn/socket";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

// 加密货币价格消息
interface CryptoPriceMessage {
    symbolId: string;
    price: number;
    timestamp: number;
}

// 价格数据点
export interface PricePoint {
    time: number;
    value: number;
}

// Store State
interface CryptoConnectState {
    currentSymbol: string | null;
    // 最新价格数据点
    latestPricePoint: PricePoint | null;
    // 最新价格
    latestPrice: number | null;
    // expo 值
    expo: number;
    // 是否已初始化连接
    initialized: boolean;
    initData: [];

    // Actions
    subscribe: ( symbol: string, expo?: number ) => void;
    unsubscribe: () => void;
    initConnection: () => void;
    clearData: () => void;

    // Internal Actions
    handlePriceMessage: ( message: CryptoPriceMessage ) => void;
}

function getWebSocketUrl(): string {
    if ( typeof window === "undefined" ) {
        return "";
    }
    const protocol = "wss";
    const wsDomain = process.env.NEXT_PUBLIC_WS_DOMAIN || "";
    const domain = process.env.NEXT_PUBLIC_DOMAIN || "";
    const cleanWsDomain = wsDomain.replace( /^https?:\/\//, "" );
    const cleanDomain = domain.replace( /^https?:\/\//, "" );
    const host = cleanWsDomain || cleanDomain || window.location.host;
    return `${protocol}://${host}${API_WS_URL}/ws/crypto/price`;
}

/**
 * 加密货币价格订阅 Store
 *
 * 使用：
 * const cryptoConnect = useCryptoConnect();
 *
 * // 初始化连接（只需调用一次）
 * cryptoConnect.initConnection();
 *
 * // 订阅价格
 * cryptoConnect.subscribe('BTC');
 *
 * // 取消订阅
 * cryptoConnect.unsubscribe();
 *
 * // 获取价格数据
 * const btcData = cryptoConnect.priceData['BTC'];
 * const latestPrice = cryptoConnect.latestPrice['BTC'];
 */
export const useCryptoConnect = create<CryptoConnectState>()(
    devtools( ( set, get ) => ( {
        currentSymbol: null,
        latestPricePoint: null,
        latestPrice: null,
        expo: 0,
        initData: [],
        initialized: false,

        initConnection: () => {
            const state = get();
            if ( state.initialized ) {
                return;
            }

            console.log( "[CryptoConnect] Initializing connection" );
            const manager = ConnectManager.getInstance( SOCKET_KEY.CRYPTO );

            // 设置订阅管理器
            manager.setSubscriptionManager( new CryptoPriceSubscriptionManager() );

            // 初始化配置并连接
            manager.init( { url: getWebSocketUrl() } );
            manager.connect();

            // 添加事件监听器
            manager.addEventListeners( socketListener );

            set( { initialized: true } );
        },

        subscribe: ( symbol: string, expo?: number ) => {
            const state = get();
            if ( !symbol ) return;

            if ( !state.initialized ) {
                console.warn( "[CryptoConnect] Connection not initialized. Call initConnection first." );
                return;
            }

            if ( state.currentSymbol === symbol ) {
                return;
            }

            if ( state.currentSymbol ) {
                get().unsubscribe();
            }

            console.log( "[CryptoConnect] Subscribing to symbol:", symbol );
            ConnectManager.getInstance( SOCKET_KEY.CRYPTO ).subscribeEvent( symbol );

            // 存储 expo 值和清空数据
            set( { currentSymbol: symbol, expo: expo || 0, latestPricePoint: null, latestPrice: null } );
        },

        unsubscribe: () => {
            const state = get();
            if ( state.currentSymbol ) {
                console.log( "[CryptoConnect] Unsubscribing from symbol:", state.currentSymbol );
                ConnectManager.getInstance( SOCKET_KEY.CRYPTO ).unsubscribeEvent( state.currentSymbol );
                set( { currentSymbol: null, latestPricePoint: null, latestPrice: null } );
            }
        },

        clearData: () => {
            set( { latestPricePoint: null, latestPrice: null, expo: 0 } );
        },

        handlePriceMessage: ( message: CryptoPriceMessage ) => {
            const { symbolId, price, timestamp } = message;
            const state = get();
            // console.log(state.currentSymbol, symbolId, "handlePriceMessage");

            // 只处理当前订阅的 symbol
            if ( String( state.currentSymbol ) !== String( symbolId ) ) {
                return;
            }

            // 转换价格: price * 10^expo
            const convertedPrice = Number( BigMath.times( price, Math.pow( 10, state.expo ), 2 ) );
            const currentPricePoint = state.latestPricePoint;

            // 检查时间戳，如果新数据的时间戳早于当前数据，则忽略
            if ( currentPricePoint && timestamp < currentPricePoint.time ) {
                console.warn( "[CryptoConnect] Ignored out-of-order message:", {
                    current: currentPricePoint.time,
                    received: timestamp,
                } );
                return;
            }

            // 同一时间点不重复写入，避免 WebSocket 重复包制造新对象触发订阅更新。
            if ( currentPricePoint && timestamp === currentPricePoint.time ) {
                return;
            }

            set( { latestPricePoint: { time: timestamp, value: convertedPrice }, latestPrice: convertedPrice } );
        },
    } ) ),
);

// Socket 监听回调
const socketListener = ( type: string, payload: any ) => {
    const store = useCryptoConnect.getState();
    // console.log( "[CryptoConnect] Received message:", { type, payload } );
    if ( type === "raw" ) {
        if ( typeof payload === "string" ) {
            const [ us, sid, price ] = payload.split( "," );
            store.handlePriceMessage( { symbolId: sid, price: +price, timestamp: +us } );
        }
    }
};
